import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import angularLocaleEn from '@angular/common/locales/en';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApplicationConfig } from '@config/application.config';
import { getDatepickerConfig } from '@config/datepicker.config';
import MessageFormatConfig from '@config/message-format.config';
import NgxSpinnerConfig from '@config/ngx-spinner.config';
import NgxsStorageConfig from '@config/ngxs-storage.config';
import StorageConfig from '@config/storage.config';
import ToastrConfig from '@config/toastr.config';
import { IconModule } from '@core/icon/icon.module';
import { LayoutModule } from '@core/layout/layout.module';
import { NavigationModule } from '@core/navigation/navigation.module';
import { LanguageVersion } from '@data/common/LanguageVersion';
import { LocaleId } from '@data/common/LocalId';
import { NgSelectConfig, NgSelectModule } from '@ng-select/ng-select';
import { StorageModule } from '@ngx-pwa/local-storage';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, NoopNgxsExecutionStrategy, Store } from '@ngxs/store';
import { AppLanguageInterceptor } from '@shared/interceptor/app-language.interceptor';
import { AppTimezoneInterceptor } from '@shared/interceptor/app-timezone.interceptor';
import { AppVersionInterceptor } from '@shared/interceptor/app-version.interceptor';
import { ErrorInterceptor } from '@shared/interceptor/error.interceptor';
import { LoaderInterceptor } from '@shared/interceptor/loader.interceptor';
import { SharedModule } from '@shared/shared.module';
import { ApplicationState } from '@shared/storage/application/application.state';
import { NavigationState } from '@shared/storage/navigation/navigation.state';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateConfig } from 'app/config/translate.config';
import { ApplicationModel } from 'app/model/application.model';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

registerLocaleData(angularLocaleEn, LanguageVersion.EN);

defineLocale('en-gb', enGbLocale);

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LayoutModule,
    StorageModule.forRoot(StorageConfig),
    TranslateModule.forRoot(TranslateConfig),
    AngularSvgIconModule.forRoot(),
    ModalModule.forRoot(),
    ProgressbarModule.forRoot(),
    ToastrModule.forRoot(ToastrConfig),
    NgSelectModule,
    BsDatepickerModule.forRoot(),
    ReactiveFormsModule,
    NavigationModule,
    NgxsModule.forRoot([
      ApplicationState,
      NavigationState
    ], {
      developmentMode: !ApplicationConfig.production,
      executionStrategy: NoopNgxsExecutionStrategy
    }),
    NgxsLoggerPluginModule.forRoot({
      disabled: ApplicationConfig.production
    }),
    NgxsStoragePluginModule.forRoot(NgxsStorageConfig),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: ApplicationConfig.production
    }),
    NgxsRouterPluginModule.forRoot(),
    NgxSpinnerModule.forRoot(NgxSpinnerConfig),
    FormsModule,
    IconModule,
    SharedModule,
    RecaptchaV3Module
  ],
  providers: [
    {
      provide: APP_INITIALIZER, multi: true,
      useFactory: (
        applicationModel: ApplicationModel
      ) => () => {
      },
      deps: [
        ApplicationModel
      ]
    },
    {
      provide: LOCALE_ID,
      useClass: LocaleId,
      deps: [ Store ]
    },
    { provide: NgSelectConfig, useValue: { disableVirtualScroll: true, openOnEnter: false } },
    { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppLanguageInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: LoaderInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppVersionInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppTimezoneInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: ErrorInterceptor },
    { provide: MESSAGE_FORMAT_CONFIG, useValue: MessageFormatConfig },
    DatePipe,
    TranslatePipe,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: '6LcPmnMrAAAAAA6S7JzNpDOQRjPs47R59HS4IpG3' },
  ],
  exports: [],
  bootstrap: [ AppComponent ]
})
export class AppModule {
  constructor() {
  }
}
